import { useCallback, useState } from 'react';
import { object, number } from 'yup';
import {
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { FormModal } from './FormModal';
import { TextInput } from '../inputs';
import { useDispatch, showNotification, showError } from '../../../state';
import { authPost } from '../../../lib';

type ExcessMode = 'item' | 'package';

interface Props {
  open: boolean;
  handleClose: () => void;
  ratesBaseUrl: string;
  onSuccess?: () => void;
  disabledModes?: ExcessMode[];
}

interface FormValues {
  mode: ExcessMode;
  startQty: number | '';
  qtyIncrement: number | '';
  rateIncrement: number | '';
}

const MAX_QTY: Record<ExcessMode, number> = {
  item: 3000,
  package: 15000,
};

const initialValues: FormValues = {
  mode: 'item',
  startQty: '',
  qtyIncrement: '',
  rateIncrement: '',
};

export function FloorLoadedExcessRatesModal({
  open,
  handleClose,
  ratesBaseUrl,
  onSuccess,
  disabledModes = [],
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const { mode, startQty, qtyIncrement, rateIncrement } = values;
      if (startQty === '' || qtyIncrement === '' || rateIncrement === '')
        return;

      const discountedRateType =
        mode === 'item' ? 'ExcessItems' : 'ExcessPackages';
      const maxQty = MAX_QTY[mode];
      const start = Number(startQty);
      const increment = Number(qtyIncrement);
      const rate = Number(rateIncrement);

      if (increment <= 0) return;

      const tiers: Array<{
        discountedRateType: string;
        quantity: number;
        rate: number;
      }> = [];
      let tierIndex = 0;
      for (let qty = start; qty <= maxQty; qty += increment) {
        tiers.push({
          discountedRateType,
          quantity: qty,
          rate: parseFloat(((tierIndex + 1) * rate).toFixed(2)),
        });
        tierIndex++;
      }

      setSubmitting(true);
      const { error } = await authPost(`${ratesBaseUrl}/bulk`, tiers);
      setSubmitting(false);

      if (error) {
        dispatch(showError({ message: 'Could not create rates' }));
      } else {
        dispatch(showNotification({ message: 'Additional rates created' }));
        onSuccess?.();
        handleClose();
      }
    },
    [dispatch, handleClose, onSuccess, ratesBaseUrl],
  );

  const defaultMode: ExcessMode = disabledModes.includes('item')
    ? 'package'
    : 'item';

  const formik = useFormik<FormValues>({
    initialValues: { ...initialValues, mode: defaultMode },
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Add additional rate</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
    >
      <FormControl
        variant='standard'
        component='fieldset'
        className={classes.radioGroup}
      >
        <RadioGroup
          name='mode'
          value={formik.values.mode}
          onChange={(e) => formik.setFieldValue('mode', e.target.value)}
          row
        >
          <FormControlLabel
            value='item'
            control={<Radio color='primary' />}
            label='Excess items'
            disabled={disabledModes.includes('item')}
          />
          <FormControlLabel
            value='package'
            control={<Radio color='primary' />}
            label='Excess packages'
            disabled={disabledModes.includes('package')}
          />
        </RadioGroup>
      </FormControl>
      <TextInput
        name='startQty'
        formikProps={formik}
        label='QTY to start billing'
        className={classes.input}
        type='number'
        required
      />
      <TextInput
        name='qtyIncrement'
        formikProps={formik}
        label='QTY to increment fees by'
        className={classes.input}
        type='number'
        required
      />
      <TextInput
        name='rateIncrement'
        formikProps={formik}
        label='Rate to increment fees by'
        className={classes.input}
        format='currency'
        required
      />
    </FormModal>
  );
}

const useStyles = makeStyles()(() => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  radioGroup: {
    marginBottom: 8,
  },
  input: {
    marginBottom: 16,
    width: 300,
  },
}));

const schema = object().shape({
  startQty: number()
    .required('Required')
    .positive('Must be greater than 0')
    .integer('Must be a whole number'),
  qtyIncrement: number()
    .required('Required')
    .min(1, 'Must be at least 1')
    .integer('Must be a whole number'),
  rateIncrement: number()
    .required('Required')
    .positive('Must be greater than 0'),
});
